import { IMultiBookingTrainOffer } from "../../Orchestrator/Recoils/MultiBookingAtom";
import { AncillaryStatusEnum, PtModeEnum, ProductCodeEnum, ProductTypeEnum, SHUTTLE_PRODUCT_CODE, ticketClassTypeEnum, TrainTypeEnum, TripTypeEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { isNolAncillary } from "./nolCard";
import { dropSupersededAdmissions, getOffersForDirection } from "./exchangeTrips";
import { IBookedOffer, IBookingResponse, ILegTrain, IOffer, IPassenger, ISelectedPassenger } from "../../Orchestrator/Types/Index";
import { IPaymentItemDetails } from "../../Orchestrator/Types/Interface/IPayment";
import { BookingPassenger, BookingTypeTrip } from "../../Orchestrator/Types/Types/BookingTypes";
import { IBookedTrainType, ITrainTypeClass, TrainType } from "../../Orchestrator/Types/Types/TrainTypes";
import { calculatePrice, convertISO8601ToDuration, convertISO8601ToMinutes, convertTimeToArabic, extractStops, getShortDateFromDateString, getTrainClassTypeBaseOnProduct } from "./validationUtil";
import dayjs from 'dayjs';

// Onward-shuttle trips share the booking (and its direction) with the train trip,
// but their admissions are not exchangeable/upgradable — aftersales payloads and
// train-trip counts must target trips that contain a TRAIN leg.
export function hasTrainLeg(trip: { legs?: any[] } | null | undefined): boolean {
    return (trip?.legs || []).some((leg: any) => leg?.timedLeg?.service?.mode?.ptMode === PtModeEnum.TRAIN);
}

// Prefer trips that contain a TRAIN leg; fall back to the full list so
// bookings whose trips carry no leg data keep the previous behavior.
export function preferTrainTrips<T extends { legs?: any[] }>(trips: T[] | null | undefined): T[] {
    const list = trips || [];
    const trainTrips = list.filter(hasTrainLeg);
    return trainTrips.length > 0 ? trainTrips : list;
}

export function getAllOfferWithoutExchange(bookedOffers: IBookedOffer[]) {
    const allBookedOfferWithoutExchange: IBookedOffer[] = []
    bookedOffers?.forEach((offer: IBookedOffer) => {
        // Guard against missing admissions array (e.g., NOL shuttle offers);
        // no admissions means no EXCHANGED status, so include the offer.
        const offerAdmissions = offer.admissions ?? [];
        const isAnyFulfilled = offerAdmissions.find(d => d.status === "EXCHANGED")
        if (!isAnyFulfilled) {
            allBookedOfferWithoutExchange.push(offer)
        }
    })
    return allBookedOfferWithoutExchange
}

/** Admission statuses meaning the passenger no longer holds the offer at all. */
const TERMINAL_ADMISSION_STATUSES = new Set<string>(["EXCHANGED", "REFUNDED", "RELEASED"]);
/** The list's original, narrower rule — kept as the cancelled-booking fallback. */
const EXCHANGED_ONLY = new Set<string>(["EXCHANGED"]);

/**
 * The booking's trips the passenger still holds, oldest first — what a bookings
 * LIST row may draw.
 *
 * The list used to keep any trip whose covering offer was not fully EXCHANGED.
 * A change-date refunds the leg's shuttle (see sessionRefundLegs.ts) and leaves
 * its trip on the booking, so that trip stayed "valid": the row merge took its
 * origin and date and drew "Shuttle … Fri 07 Aug" as the passenger's next
 * ticket, while the train they had just moved to went unshown. RELEASED is dead
 * the same way — the travel right is gone either way.
 *
 * `getAllOfferWithoutExchange` is deliberately NOT widened to cover this: four
 * other callers depend on its current behaviour, and the list renders a fully
 * refunded booking on purpose with a REFUNDED badge (`resolveOfferStatus`).
 * Hence the fallback here — when NOTHING is live, hand back the old
 * EXCHANGED-only selection so a cancelled booking still has something to draw.
 *
 * A trip survives on its offers' behalf only when an offer still has a
 * non-terminal admission. A shuttle offer carries one admission per rider, so
 * refunding one passenger must not hide the trip from those who kept theirs.
 * An offer with no admissions proves nothing and counts as live.
 */
export function tripsStillHeld<T extends { id: string; startTime: string }>(
    trips: T[],
    bookedOffers: IBookedOffer[],
): T[] {
    const byStartTime = (a: T, b: T) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime();

    const keep = (trip: T, deadStatuses: Set<string>) =>
        (bookedOffers || []).some((offer) => {
            if (offer?.tripCoverage?.coveredTripId !== trip.id) return false;
            const admissions = offer.admissions;
            // Reproduces the replaced rule EXACTLY, and the two empty cases
            // differ there:
            //   `[]`        — `[].every()` is `true`, so it read as fully dead
            //                 and did NOT vouch for the trip.
            //   `undefined` — short-circuited by `?? false`, so it DID vouch.
            // Keeping that distinction matters in both directions. An
            // ancillary-only offer with `admissions: []` can cover the ORIGINAL
            // train trip; letting it vouch would re-admit that trip after the
            // exchange and put the old date back on the row — the bug this
            // function exists to kill. Meanwhile `getAllOfferWithoutExchange`
            // (above) documents that offers with no admissions array at all
            // exist (NOL shuttle offers), and those must still render.
            if (admissions === undefined) return true;
            if (admissions.length === 0) return false;
            return !admissions.every((admission) =>
                deadStatuses.has((admission as { status?: string })?.status || "")
            );
        });

    const live = (trips || []).filter((trip) => keep(trip, TERMINAL_ADMISSION_STATUSES));
    if (live.length > 0) return [...live].sort(byStartTime);
    return [...(trips || [])]
        .filter((trip) => keep(trip, EXCHANGED_ONLY))
        .sort(byStartTime);
}

export function getAllTripWithoutExchange(bookedOffers: IBookedOffer[], trips: BookingTypeTrip[]) {
    const allTripCoverageIds = bookedOffers.map(offer => offer?.tripCoverage?.coveredTripId)
    const allTripWithoutExchange: BookingTypeTrip[] = trips?.filter(trip => allTripCoverageIds.includes(trip.id))
    return allTripWithoutExchange
}

/**
 * The admission product code the booking already holds for a given direction —
 * i.e. the fare tier the user is on (STV / SFF / PFF…). Resolves the offer by
 * its trip coverage (never positional index, which isn't guaranteed for round
 * trips) and reads the ADMISSION product, skipping ancillary/reservation
 * products that may precede it in `products`. Returns undefined when the
 * booking has no trip in that direction (e.g. inbound on a one-way booking).
 */
export function getBookedAdmissionCodeByDirection(
    booking: IBookingResponse | null | undefined,
    direction: TripTypeEnum,
): string | undefined {
    const trips = booking?.booking?.trips ?? [];
    const tripId = trips.find((trip) => trip.direction === direction)?.id;
    if (!tripId) return undefined;
    const offers = booking?.booking?.bookedOffers ?? [];
    const offer =
        offers.find((bookedOffer) => bookedOffer.tripCoverage?.coveredTripId === tripId) ??
        (offers.length === 1 ? offers[0] : undefined);
    return offer?.products?.find((product) => product.type === ProductTypeEnum.ADMISSION_POINT2POINT)?.code;
}

export function getShuttleTripIds(bookedOffers: any) {
    return bookedOffers
        .filter((offer: any) => offer.products?.[0]?.code === SHUTTLE_PRODUCT_CODE)
        .map((offer: any) => offer.tripCoverage.coveredTripId);
}

export const getPassengerListByOfferAdmissions = async (
    passengers: ISelectedPassenger[],
    allTrip: BookingTypeTrip[],
    bookedOffers: IBookedOffer[]
) => {
    const passengerList: BookingPassenger = {
        outbound: [],
        inbound: []
    }
    // const outBoundTrip = allTrip.reduce((minTrip, trip) => new Date(trip.startTime) < new Date(minTrip.startTime) ? trip : minTrip)
    // const inBoundTrip = allTrip.length > 1 ? (allTrip.reduce((maxTrip, trip) => new Date(trip.startTime) > new Date(maxTrip.startTime) ? trip : maxTrip)) : null
    // Same mid-exchange hazard as the shared action: reading only the first trip
    // in a direction prices the passenger off the ticket they are exchanging
    // away. See getOffersForDirection / dropSupersededAdmissions.
    const outBoundOffers = getOffersForDirection(allTrip, bookedOffers, TripTypeEnum.OUT_BOUND)
    const inBoundOffers = getOffersForDirection(allTrip, bookedOffers, TripTypeEnum.IN_BOUND)

    if (outBoundOffers.length > 0) {
        {
            const allTheOutBoundPassengerId = outBoundOffers.flatMap(offer => (offer?.admissions || []).map(add => add.passengerIds?.[0]))
            passengerList.outbound = JSON.parse(JSON.stringify([...passengers])).filter((d: ISelectedPassenger) => allTheOutBoundPassengerId?.includes(d.id))
                .map((pass: ISelectedPassenger) => {
                    if (allTheOutBoundPassengerId?.includes(pass.id)) {
                        const passengerAdmission = dropSupersededAdmissions(
                            outBoundOffers.flatMap(offer => (offer?.admissions || []).filter(add => add.passengerIds?.includes(pass.id)))
                        )[0]
                        if (passengerAdmission) {
                            pass.passengerPrice = calculatePrice(passengerAdmission.price)
                            pass.passengerStatus = passengerAdmission.status
                        }
                    }
                    return pass
                })
        }
    }

    if (inBoundOffers.length > 0) {
        {
            const allTheInBoundPassengerId = inBoundOffers.flatMap(offer => (offer?.admissions || []).map(add => add.passengerIds?.[0]))
            passengerList.inbound = JSON.parse(JSON.stringify([...passengers])).filter((d: ISelectedPassenger) => allTheInBoundPassengerId?.includes(d.id))
                .map((pass: ISelectedPassenger) => {
                    if (allTheInBoundPassengerId?.includes(pass.id)) {
                        const passengerAdmission = dropSupersededAdmissions(
                            inBoundOffers.flatMap(offer => (offer?.admissions || []).filter(add => add.passengerIds?.includes(pass.id)))
                        )[0]
                        if (passengerAdmission) {
                            pass.passengerPrice = calculatePrice(passengerAdmission.price)
                            pass.passengerStatus = passengerAdmission.status
                        }
                    }
                    return pass
                })
        }
    }
    return passengerList
}

export function checkBookingPlanBaseOnSummary(summary: string): "standardValue" | "standardPro" | "premiumValue" | string {
    let classType: string = ""
    if (summary?.toLocaleLowerCase().includes("standard flexible")) {
        if (summary?.toLocaleLowerCase().includes("with fee")) {
            classType = "standardValue"
        } else {
            classType = "standardPro"
        }
    }
    if (summary?.toLocaleLowerCase().includes("premium"))
        classType = "premiumValue"

    return classType
}

export const getPassengerFromBooking = (passengers: IPassenger[]): ISelectedPassenger[] => {
    let bookingPassengerList: ISelectedPassenger[] = []
    try {
        bookingPassengerList = passengers.map((passenger: IPassenger) => {
            const passengerObject: ISelectedPassenger = {
                id: passenger.id,
                firstName: passenger?.detail?.firstName || "",
                lastName: passenger?.detail?.lastName || "",
                email: passenger?.detail?.email || "",
                mobile: "",
                remember: false,
                age: passenger.age,
                seatingDetails: {
                    coach: "",
                    seats: []
                },
                cards: passenger.cards || [],
                prmNeeds: passenger.prmNeeds || [],
            }
            return passengerObject
        });
    } catch (error) {
        console.error("getPassengerFromBooking=>", (error as Error).message);
    } finally {
        return bookingPassengerList
    }
}


export const getPassengerDataFromLeg = (bookingPassengerList: ISelectedPassenger[], legId: string, bookingData?: any): ISelectedPassenger[] => {
    try {
        // Flatten once: post-exchange the NOL product an ancillary references
        // can live on a sibling offer, not just the offer that owns it.
        const allProducts = ((bookingData?.bookedOffers || []) as any[]).flatMap((o) => o?.products || []);
        return bookingPassengerList.map((passenger) => {
            let coach = "";
            let seats: any = [];

            // Step 1: Find matching offer for this passenger
            let matchingOffer: Partial<IBookedOffer> = {};

            bookingData?.bookedOffers?.some((offer: any) => {
                const reservations = offer.reservations || [];
                const admissions = offer.admissions || [];

                const reservationMatch = reservations.some((res: any) => {
                    const coveredLegIds = res.tripCoverage?.coveredLegIds || [];
                    const passengerIds = res.passengerIds || [];

                    return coveredLegIds.includes(legId) && passengerIds.includes(passenger.id);
                });

                if (reservationMatch) {
                    matchingOffer = offer;
                    return true;
                }

                const admissionMatch = admissions.some((admission: any) => {
                    const coveredLegIds = admission.tripCoverage?.coveredLegIds || [];
                    const passengerIds = admission.passengerIds || [];

                    return coveredLegIds.includes(legId) && passengerIds.includes(passenger.id);
                });

                if (admissionMatch) {
                    matchingOffer = offer;
                    return true;
                }

                return false;
            });

            // Step 2: Find reservation with seat info for this leg
            const reservation = matchingOffer?.reservations?.find((res: any) =>
                res.tripCoverage?.coveredLegIds?.includes(legId) &&
                res.placeAllocation?.reservedPlaces?.some((place: any) =>
                    place.passengerIds.includes(passenger.id)
                )
            );

            let passengerStatus = reservation?.status;

            let matchingAdmission: any = null;
            if (!passengerStatus) {
                matchingAdmission = matchingOffer?.admissions?.find((admission: any) =>
                    admission.tripCoverage?.coveredLegIds?.includes(legId) &&
                    admission.passengerIds?.includes(passenger.id)
                );

                passengerStatus = matchingAdmission?.status;

                if (!passengerStatus && matchingAdmission?.id) {
                    const fulfillmentStatus = bookingData?.fulfillments?.find((fulfillment: any) => fulfillment.id === matchingAdmission.id)?.status;
                    if (fulfillmentStatus) {
                        passengerStatus = fulfillmentStatus;
                    }
                }
            }

            if (reservation) {
                const reservedPlace = reservation.placeAllocation?.reservedPlaces?.find((place: any) =>
                    place.passengerIds.includes(passenger.id)
                );

                if (reservedPlace) {
                    coach = reservedPlace.coachNumber || "";
                    seats = reservedPlace.placeNumbers || [];
                }
            }

            const addonsDetails: string[] = []
            const passengerAncillaries = (matchingOffer?.ancillaries || []).filter((res: any) =>
                res.tripCoverage?.coveredLegIds?.includes(legId) &&
                res.passengerIds.includes(passenger.id)
            );
            if (passengerAncillaries.length > 0) {
                for (const ancillaries of passengerAncillaries) {
                    // Trip summary card lists what's actually on the booking
                    // RIGHT NOW — i.e. paid (FULFILLED). Skip:
                    //   - REFUNDED: not on the booking anymore.
                    //   - PREBOOKED: orphan reservation from an abandoned
                    //     manage-seating / addons session that the user
                    //     never paid for. These get cleaned up by
                    //     cleanupOrphanAddonsAction on the next flow entry,
                    //     but until that runs we don't want them rendered
                    //     as if they were paid extras.
                    if (ancillaries.status !== AncillaryStatusEnum.FULFILLED) continue
                    // NOL passes ride the leg as ancillaries too, but they have
                    // their own End-to-end Journey card — never the addons line.
                    if (isNolAncillary(allProducts, ancillaries)) continue
                    const productId = ancillaries.products[0].productId
                    const productDetails = (matchingOffer?.products || []).find((d: any) => d.id === productId)
                    if (productDetails?.description) {
                        addonsDetails.push(productDetails.description)
                    }
                }
            }

            return {
                ...passenger,
                addonsDetails: addonsDetails,
                seatingDetails: {
                    coach,
                    seats
                },
                passengerStatus: passengerStatus
            };
        });
    } catch (error) {
        console.error("getPassengerDataFromLeg =>", (error as Error).message);
        return bookingPassengerList;
    }
}

export function checkBookingClassCodeBaseOnSummary(summary: string): "standardValue" | "standardPro" | "premiumValue" | string {
    let classType: string = ""
    if (summary == "standardValue")
        classType = ProductCodeEnum.SFC
    else if (summary == "standardPro")
        classType = ProductCodeEnum.SFF
    else if (summary == "premium")
        classType = ProductCodeEnum.PFF
    return classType
}


type TransitMode = "WALKING" | "TRAIN" | "METRO" | "SHUTTLE" | "BUS" | "CAR" | "DRIVING" | "EV_DRIVING" | "FERRY" | "LIGHTRAIL";

export const getTransitIconPath = (
    transitMode: string,
    color?: "dark" | "red"
) => {
    const ICONS: Record<TransitMode, { dark: string; red: string }> = {
        WALKING: {
            dark: "/assets/fmlm/user_fmlm.svg",
            red: "/assets/fmlm/user_fmlm_red.svg"
        },
        TRAIN: {
            dark: "/assets/fmlm/train_fmlm.svg",
            red: "/assets/fmlm/train_fmlm_red.svg"
        },
        METRO: {
            dark: "/assets/fmlm/metro_fmlm.svg",
            red: "/assets/fmlm/metro_fmlm_red.svg"
        },
        SHUTTLE: {
            dark: "/assets/fmlm/shuttle_fmlm.svg",
            red: "/assets/fmlm/shuttle_fmlm_red.svg"
        },
        BUS: {
            dark: "/assets/fmlm/bus_fmlm.svg",
            red: "/assets/fmlm/bus_fmlm_red.svg"
        },
        CAR: {
            dark: "/assets/fmlm/car_fmlm.svg",
            red: "/assets/fmlm/car_fmlm_red.svg"
        },
        DRIVING: {
            dark: "/assets/fmlm/driving_fmlm.svg",
            red: "/assets/fmlm/driving_fmlm_red.svg"
        },
        EV_DRIVING: {
            dark: "/assets/fmlm/ev_driving_fmlm.svg",
            red: "/assets/fmlm/ev_driving_fmlm_red.svg"
        },
        FERRY: {
            dark: "/assets/fmlm/ferry_fmlm.svg",
            red: "/assets/fmlm/ferry_fmlm_red.svg"
        },
        LIGHTRAIL: {
            dark: "/assets/fmlm/lightrail_fmlm.svg",
            red: "/assets/fmlm/lightrail_fmlm_red.svg"
        }
    }

    const selectedColor = color || "dark";
    let mode: TransitMode = transitMode as TransitMode;
    if (!ICONS[mode]) {
        console.warn(`Transit mode "${transitMode}" not found. Defaulting to "WALKING".`);
        mode = "WALKING";
    }

    return ICONS[mode][selectedColor];
};

export const getSelectedProductPriceFromTrainType = (train: TrainType, priceClass: ITrainTypeClass) => {
    let price = 0
    if (priceClass === ticketClassTypeEnum.standardValue) {
        price = train?.priceInfo.standardValue.price ?? 0;
    } else if (priceClass === ticketClassTypeEnum.standardPro) {
        price = train?.priceInfo.standardPro.price ?? 0;
    } else if (priceClass === ticketClassTypeEnum.premium) {
        price = train?.priceInfo.premium.price ?? 0;
    }
    return price;
}

export const getSelectedProductOffersFromTrainType = (train: TrainType): IOffer[] | undefined => {
    let priceCategory: IOffer[] | undefined;
    const selectedPriceClass = train.selectedPriceClass;

    if (!selectedPriceClass) {
        return undefined;
    }

    if (selectedPriceClass === ticketClassTypeEnum.standardValue) {
        priceCategory = train.standardOffers;
    } else if (selectedPriceClass === ticketClassTypeEnum.standardPro) {
        priceCategory = train.standardProOffers;
    } else if (selectedPriceClass === ticketClassTypeEnum.premium) {
        priceCategory = train.premiumOffers;
    }

    return priceCategory;
}

export const getBookedTrainTypeFromBooking = (bookingResponseState: IBookingResponse | null) => {
    let trips = bookingResponseState?.booking.trips || [];
    const bookedOffers = bookingResponseState?.booking.bookedOffers || [];

    const allBookedOfferWithoutExchange = getAllOfferWithoutExchange(bookedOffers);

    let allTrains: IBookedTrainType[] = trips.map((trip) => {

        const bookedOffers = allBookedOfferWithoutExchange.filter(offer => offer.tripCoverage.coveredTripId === trip.id);

        const bookingPassengerList = getPassengerFromBooking(bookingResponseState?.booking.passengers || []);

        let bookedTrainType: IBookedTrainType = {
            id: trip.id,
            departureTime: convertTimeToArabic(trip.startTime),
            arrivalTime: convertTimeToArabic(trip.endTime),
            type: trip.direction === TripTypeEnum.OUT_BOUND ? TrainTypeEnum.outbound : TrainTypeEnum.inbound,
            duration: convertISO8601ToDuration(trip.duration),
            durationInMinutes: convertISO8601ToMinutes(trip.duration),
            trainNumbers: trip?.legs[0]?.timedLeg?.service?.vehicleNumbers?.[0] || '',
            priceClass: getTrainClassTypeBaseOnProduct(bookedOffers[0]?.products || []) || "standardValue",
            trainNo: trip?.legs[0]?.timedLeg?.service?.vehicleNumbers?.[0] || '',
            transfers: trip.transfers,
            shortDate: trip.startTime ? dayjs.isDayjs(trip.startTime) ? trip.startTime.toISOString() : String(trip.startTime) : '',
            bookingCode: bookingResponseState?.booking.bookingCode || '',
            descriptionText: bookedOffers[0]?.products[0]?.descriptiveTexts[0]?.description,
            bookedOffers: bookedOffers,
            stops: extractStops(trip),
            legs: trip.legs,
            from: trip.origin.stopPlaceRef,
            to: trip.destination.stopPlaceRef,
            legTrains: trip.legs.map((leg) => {
                const admissionProduct = bookedOffers[0]?.products?.find((p: any) => p.type === ProductTypeEnum.ADMISSION_POINT2POINT) || bookedOffers[0]?.products?.[0];
                const legTrain: ILegTrain = {
                    legId: leg.id,
                    origin: leg.timedLeg?.start?.stopPlaceRef || "",
                    destination: leg.timedLeg?.end?.stopPlaceRef || "",
                    fromName: leg?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || "",
                    toName: leg?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef || "",
                    trainType: leg?.timedLeg?.service?.mode?.ptMode == "TRAIN" ? "train" : "shuttle",
                    duration: convertISO8601ToDuration(leg?.timedLeg?.duration || ""),
                    startTime: leg?.timedLeg?.start?.serviceDeparture?.timetabledTime || "",
                    endTime: leg?.timedLeg?.end?.serviceArrival?.timetabledTime || "",
                    shortStartDate: getShortDateFromDateString(leg?.timedLeg?.start?.serviceDeparture?.timetabledTime || ""),
                    shortEndDate: getShortDateFromDateString(leg?.timedLeg?.end?.serviceDeparture?.timetabledTime || ""),
                    departureTime: convertTimeToArabic(leg?.timedLeg?.start?.serviceDeparture?.timetabledTime || ""),
                    arrivalTime: convertTimeToArabic(leg?.timedLeg?.end?.serviceArrival?.timetabledTime || ""),
                    stops: [],// trip.transfers > 0 ? Array(trip.transfers).fill('Stop') : [],
                    transfers: 0,// trip.transfers,
                    trainNo: `${leg?.timedLeg?.service?.vehicleNumbers?.[0] || ''}`,
                    trainClass: leg?.timedLeg?.service?.mode?.ptMode == "TRAIN" ? trip?.priceClass || "" : "",
                    productName: admissionProduct?.summary || admissionProduct?.description || "",
                    passengers: getPassengerDataFromLeg(bookingPassengerList, leg.id, bookingResponseState?.booking),  //[],// tripPassenger?.outbound || []
                    shortStartDateFormat: leg?.timedLeg?.start?.serviceDeparture?.timetabledTime,
                    shortEndDateFormat: leg?.timedLeg?.end?.serviceDeparture?.timetabledTime,
                };
                return legTrain;
            }),
        }

        return bookedTrainType
    })

    return allTrains;
}

export const isBookingMultiBooking = (booking: IBookingResponse | null): boolean => {
    return booking?.booking?.bookedOffers?.some(
        (offer: any) => offer.appliedThroughTicketTags?.includes("MultiBooking")
    ) || false;
}
